import { HubConnectionState } from '@microsoft/signalr';
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';

import {
    AI_RESULT_EVENT,
    createAiHubConnection,
    type AiResultNotification,
} from '@/api/ai-gateway/aiHub';

type AiResultListener = (notification: AiResultNotification) => void;

interface AiResultContextValue {
    /** Subscribe to every AI result push; returns an unsubscribe. Filter by correlationId yourself. */
    subscribe: (listener: AiResultListener) => () => void;
    /** Returns the last cached result for a correlationId, if it already arrived before subscribe. */
    getCached: (correlationId: string) => AiResultNotification | undefined;
}

/**
 * Exported so a test can tell "no provider above me" from "a provider that answered nothing",
 * which `useAiResultContext` cannot: it throws instead of returning.
 */
export const AiResultContext = createContext<AiResultContextValue | null>(null);

interface AiResultProviderProps {
    children: ReactNode;
    /** Only opens the connection when true (e.g. an authenticated user is present). */
    enabled?: boolean;
}

/**
 * Owns a single AI hub connection for the app and fans each pushed result out to subscribers. One
 * connection is shared by all consumers (via {@link useAiResult}); mounting this once, high in the
 * tree, is enough.
 */
export function AiResultProvider({ children, enabled = true }: AiResultProviderProps) {
    // A stable registry so re-renders never tear down subscriptions or the connection.
    const listenersRef = useRef(new Set<AiResultListener>());
    // Cache the latest result per correlationId so late subscribers don't miss fast responses.
    const lastByCorrelationIdRef = useRef(new Map<string, AiResultNotification>());

    const value = useMemo<AiResultContextValue>(
        () => ({
            subscribe: (listener) => {
                listenersRef.current.add(listener);
                return () => {
                    listenersRef.current.delete(listener);
                };
            },
            getCached: (correlationId) => lastByCorrelationIdRef.current.get(correlationId),
        }),
        [],
    );

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const connection = createAiHubConnection();
        connection.on(AI_RESULT_EVENT, (notification: AiResultNotification) => {
            if (notification.correlationId) {
                lastByCorrelationIdRef.current.set(notification.correlationId, notification);
            }
            listenersRef.current.forEach((listener) => listener(notification));
        });

        // Retry the initial start on failure; withAutomaticReconnect only handles drops after a
        // successful start, so a failed handshake (e.g. token not yet in localStorage) otherwise stays
        // broken for the whole session. A fixed 10s retry gives the token/connection time to land.
        let cancelled = false;
        const startWithRetry = async () => {
            if (cancelled || connection.state !== HubConnectionState.Disconnected) {
                return;
            }
            try {
                await connection.start();
            } catch {
                if (!cancelled) {
                    window.setTimeout(startWithRetry, 10_000);
                }
            }
        };
        void startWithRetry();

        return () => {
            cancelled = true;
            connection.off(AI_RESULT_EVENT);
            void connection.stop();
        };
    }, [enabled]);

    return <AiResultContext.Provider value={value}>{children}</AiResultContext.Provider>;
}

export function useAiResultContext(): AiResultContextValue {
    const context = useContext(AiResultContext);
    if (!context) {
        throw new Error('useAiResult must be used within an <AiResultProvider>.');
    }

    return context;
}
